import { useEffect, useMemo, useState } from "react";
import { revealProps } from "../../common/Reveal";
import { useNavigate } from "react-router-dom";
import { StarIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import AiDisclaimer from "../AiDisclaimer";
import { SortableTh, SortState } from "../../common/ListViewControls";
import { useLocalStorageState } from "../../../hooks/useLocalStorageState";
import {
  adjustedRelevance,
  api,
  CallListItem,
  formatMs,
  PIPELINE_LABELS,
  scoreClass,
  callParties,
} from "../api";
import AnalyzingState from "../AnalyzingState";
import Tooltip from "../../common/Tooltip";
import { clearingClass, isNewSince, UpdatesBanner } from "../updates";

type SortKey = "relevance" | "date" | "duration" | "leads" | "name";

const CallsTab: React.FC<{
  investigationId: string;
  processing: number;
  reanalyzing?: boolean;
  updates?: number;
  since?: string;
  onSeen?: () => void;
  clearing?: boolean;
}> = ({ investigationId, processing, reanalyzing, updates = 0, since, onSeen, clearing }) => {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<CallListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useLocalStorageState<SortState<SortKey>>(
    "investigations.calls.sort",
    { key: "relevance", dir: "desc" },
  );

  const load = async () => {
    try {
      setCalls(await api.calls(investigationId));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investigationId]);
  useEffect(() => {
    if (
      !processing &&
      !calls.some((c) => !["complete", "failed"].includes(c.pipeline_stage))
    )
      return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing, calls]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? calls.filter((c) =>
          [
            c.file.original_file_name,
            callParties(c).primary,
            c.to_number,
            c.summary,
          ].some((v) => (v || "").toLowerCase().includes(q)),
        )
      : calls;
    return [...list].sort((a, b) => {
      let cmp = 0;
      switch (sort.key) {
        case "relevance":
          cmp = (adjustedRelevance(a) ?? -1) - (adjustedRelevance(b) ?? -1);
          break;
        case "date":
          cmp =
            new Date(a.call_started_at || 0).getTime() -
            new Date(b.call_started_at || 0).getTime();
          break;
        case "duration":
          cmp = (a.duration_ms || 0) - (b.duration_ms || 0);
          break;
        case "leads":
          cmp = a.leadCount - b.leadCount;
          break;
        case "name":
          cmp = a.file.original_file_name.localeCompare(
            b.file.original_file_name,
          );
      }
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [calls, search, sort]);

  const analyzed = calls.filter((c) => c.pipeline_stage === "complete").length;

  return (
    <div>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          className="input input-sm input-bordered bg-base-100 w-64"
          placeholder="Search calls..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="text-sm text-base-content/60">
          {analyzed} of {calls.length} analyzed
          {calls.length - analyzed > 0 && (
            <span className="text-amber-700 dark:text-amber-400">
              {" "}
              · {calls.length - analyzed} in progress
            </span>
          )}
        </span>
      </div>
      {reanalyzing && (
        <AnalyzingState
          compact
          title="Re-analyzing every call"
          body="Scores and summaries update per call as each one finishes. You can still open and play calls."
        />
      )}
      {calls.length > 0 && <AiDisclaimer kind="callsList" className="mb-3" />}
      {onSeen && <UpdatesBanner tab="calls" count={updates} since={since} onDismiss={onSeen} clearing={clearing} className="mb-3" />}
      {loading ? (
        <div className="animate-pulse h-48 bg-base-300 rounded-xl" />
      ) : calls.length === 0 ? (
        <div className="rounded-xl border border-dashed border-base-300 p-10 text-center text-base-content/60">
          No calls yet. Upload jail call audio on the Files tab.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-base-300">
          <table className="table table-sm">
            <thead>
              <tr>
                <SortableTh
                  label="Relevance"
                  sortKey="relevance"
                  sort={sort}
                  onSort={setSort}
                  defaultDir="desc"
                />
                <SortableTh
                  label="Call"
                  sortKey="name"
                  sort={sort}
                  onSort={setSort}
                />
                <SortableTh
                  label="Date"
                  sortKey="date"
                  sort={sort}
                  onSort={setSort}
                  defaultDir="desc"
                />
                <th>Participants</th>
                <SortableTh
                  label="Length"
                  sortKey="duration"
                  sort={sort}
                  onSort={setSort}
                  defaultDir="desc"
                />
                <SortableTh
                  label="Flags"
                  sortKey="leads"
                  sort={sort}
                  onSort={setSort}
                  defaultDir="desc"
                />
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c, i) => (
                <tr
                  key={c.id}
                  style={revealProps(i).style}
                  className={cn("reveal hover cursor-pointer transition-[background-color] duration-[650ms] ease-out", isNewSince(c.analyzed_at, since) && !clearing && "bg-blue-50/60 dark:bg-blue-900/20")}
                  onClick={() =>
                    navigate(`/investigations/${investigationId}/calls/${c.id}`)
                  }
                >
                  <td className="relative">
                    {isNewSince(c.analyzed_at, since) && <span className={cn("absolute left-0 top-0 bottom-0 w-0.5 bg-blue-300 dark:bg-blue-700", clearingClass(clearing))} aria-hidden />}
                    <Tooltip
                      placement="top"
                      overlay={
                        adjustedRelevance(c) !== c.relevance_score
                          ? `AI score ${c.relevance_score}, adjusted for ${c.dismissedCount} dismissed and ${c.confirmedCount} confirmed lead${c.totalLeads === 1 ? "" : "s"}`
                          : "AI relevance score"
                      }
                    >
                      <span
                        className={cn(
                          "inline-block min-w-[2.5rem] text-center px-2 py-0.5 rounded-full text-xs font-bold",
                          scoreClass(adjustedRelevance(c)),
                        )}
                      >
                        {adjustedRelevance(c) ?? "—"}
                      </span>
                    </Tooltip>
                    {c.is_starred && (
                      <StarIcon className="size-4 text-amber-500 inline ml-1" />
                    )}
                  </td>
                  <td className="max-w-md">
                    <p className="font-medium text-base-content truncate">
                      {c.file.original_file_name}
                    </p>
                    {c.summary && (
                      <p className="text-xs text-base-content/60 line-clamp-2">
                        {c.relevance_reason || c.summary}
                      </p>
                    )}
                  </td>
                  <td className="text-xs whitespace-nowrap text-base-content/70">
                    {c.call_started_at ? (
                      <>
                        <span className="block text-base-content/80">
                          {new Date(c.call_started_at).toLocaleDateString()}
                        </span>
                        <span className="block text-base-content/50">
                          {new Date(c.call_started_at).toLocaleTimeString([], {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </span>
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="text-xs text-base-content/70 whitespace-nowrap">
                    <span className="block text-base-content">
                      {callParties(c).inmate}
                    </span>
                    {callParties(c).others.map((o) => (
                      <span key={o} className="block text-base-content/70">
                        → {o}
                        {c.suggested_outside && !callParties(c).secondary && (
                          <span
                            className="ml-1 italic text-base-content/50"
                            title={`Heard on ${c.suggested_outside.support} other call${c.suggested_outside.support === 1 ? "" : "s"} to this number — open the call to confirm`}
                          >
                            probably {c.suggested_outside.name}?
                          </span>
                        )}
                      </span>
                    ))}
                    {callParties(c).secondary && (
                      <span className="block text-[11px] text-base-content/50 tabular-nums">
                        {callParties(c).secondary}
                      </span>
                    )}
                    {c.has_third_party && (
                      <span className="ml-1 text-[10px] px-1 rounded bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-400">
                        3-way
                      </span>
                    )}
                    {c.primary_language && !/^en/i.test(c.primary_language) && (
                      <span className="ml-1 text-[10px] px-1 rounded bg-base-200">
                        {c.primary_language}
                      </span>
                    )}
                  </td>
                  <td className="text-xs tabular-nums">
                    {c.duration_ms ? formatMs(c.duration_ms) : "—"}
                  </td>
                  <td className="whitespace-nowrap align-middle">
                    {c.leadCount > 0 && (
                      <Tooltip
                        placement="top"
                        overlay={
                          <div className="text-xs leading-5">
                            <p className="font-semibold">
                              {c.leadCount} flagged moment
                              {c.leadCount === 1 ? "" : "s"}
                            </p>
                            {c.confirmedCount > 0 && (
                              <p>{c.confirmedCount} confirmed</p>
                            )}
                            {c.dismissedCount > 0 && (
                              <p>{c.dismissedCount} dismissed</p>
                            )}
                            {c.leadCount - c.confirmedCount - c.dismissedCount >
                              0 && (
                              <p>
                                {c.leadCount -
                                  c.confirmedCount -
                                  c.dismissedCount}{" "}
                                still to review
                              </p>
                            )}
                          </div>
                        }
                      >
                        <div className="inline-flex flex-col items-center gap-1">
                          <span
                            className={cn(
                              "min-w-[2.5rem] text-center text-xs font-semibold px-2 py-0.5 rounded-full",
                              c.topSeverity >= 4
                                ? "bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400"
                                : "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-400",
                            )}
                          >
                            {c.leadCount}
                          </span>
                          {/* Triage bar: how much of this call's flags the detective has already decided on. */}
                          {(c.confirmedCount > 0 || c.dismissedCount > 0) && (
                            <>
                              <span className="flex w-10 h-1 rounded-full overflow-hidden bg-base-300">
                                <span
                                  className="h-full bg-green-500"
                                  style={{
                                    width: `${(c.confirmedCount / c.leadCount) * 100}%`,
                                  }}
                                />
                                <span
                                  className="h-full bg-base-content/25"
                                  style={{
                                    width: `${(c.dismissedCount / c.leadCount) * 100}%`,
                                  }}
                                />
                              </span>
                              <span className="text-[10px] leading-none tabular-nums text-base-content/50">
                                {c.confirmedCount > 0 && (
                                  <span className="text-green-700 dark:text-green-400">
                                    {c.confirmedCount}✓
                                  </span>
                                )}
                                {c.confirmedCount > 0 &&
                                  c.dismissedCount > 0 && (
                                    <span className="mx-0.5">·</span>
                                  )}
                                {c.dismissedCount > 0 && (
                                  <span>{c.dismissedCount}✕</span>
                                )}
                              </span>
                            </>
                          )}
                        </div>
                      </Tooltip>
                    )}
                  </td>
                  <td className="text-xs">
                    {c.pipeline_stage === "failed" ? (
                      <span
                        className="text-red-600 dark:text-red-400"
                        title={c.error_message || c.file.error_message || ""}
                      >
                        Failed
                      </span>
                    ) : c.pipeline_stage === "complete" ? (
                      <span
                        className={cn(
                          c.review_status === "unreviewed"
                            ? "text-base-content/60"
                            : "text-green-700 dark:text-green-400",
                        )}
                      >
                        {c.review_status.replace("_", " ")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400">
                        <span className="size-2 rounded-full bg-amber-500 animate-pulse" />
                        {PIPELINE_LABELS[c.pipeline_stage]}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CallsTab;
